import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Badge, Button, DatePicker, Drawer, Plus, Radio, Select, TextInput, Trash01 } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { parseDateInput, toDate, toValue } from "../../ClassScheduleWizard/dateValue";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import { useStaffingPeopleContext } from "./StaffingPeopleContext";
import { errorMessages } from "./staffingFormatters";
import { inScope } from "./staffingScopes";
import RecentTimeCards from "./RecentTimeCards";

const PIN_PATTERN = /^\d{4,6}$/;

function pinHint(person) {
  if (person.pin_shared) return "Someone else on the team has this PIN, so it finds no one at the kiosk. Enter 4 to 6 digits to replace it.";
  if (person.pin_set) return "A PIN is set. Enter 4 to 6 digits to replace it. We text the new PIN to them if they get texts.";
  return "4 to 6 digits, used to clock in and out. We text it to them if they get texts.";
}

const initialForm = (person) => ({
  first_name: person.first_name || "",
  last_name: person.last_name || "",
  email: person.email || "",
  phone: person.phone || "",
  hired_on: person.hired_on || "",
  payroll_external_id: person.payroll_external_id || "",
  pin: "",
});

const initialPositions = (person) =>
  (person.positions || []).map((position) => ({
    key: `existing-${position.id}`,
    id: position.id,
    staff_position_id: position.staff_position_id,
    primary: position.primary,
    hourly_rate: position.hourly_rate ?? "",
  }));

const PersonDrawer = ({ person, onClose, onSaved }) => {
  const { permissions, permissionScopes, filterOptions, paths } = useStaffingPeopleContext();
  const managePositionIds = permissionScopes?.managePositionIds;
  const ratePositionIds = permissionScopes?.ratePositionIds;
  const isNew = !person.id;
  const detailsLocked = !isNew && !person.can_edit_details;
  const [form, setForm] = useState(() => initialForm(person));
  const [positions, setPositions] = useState(() => initialPositions(person));
  const [removedIds, setRemovedIds] = useState([]);
  const [venueIds, setVenueIds] = useState(() => (person.venues || []).map((venue) => venue.venue_ownership_id));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const positionOptions = useMemo(() => {
    const options = filterOptions.positions.filter((option) => inScope(managePositionIds, option.value));
    (person.positions || []).forEach((position) => {
      if (!options.some((option) => option.value === position.staff_position_id)) {
        options.push({ value: position.staff_position_id, label: position.name, volunteer: position.volunteer });
      }
    });
    return options;
  }, [filterOptions.positions, person.positions, managePositionIds]);

  const canSetRate = (staffPositionId) => permissions.manageRates && inScope(ratePositionIds, staffPositionId);
  const lockedRow = (row) => Boolean(row.id) && !inScope(managePositionIds, row.staff_position_id);
  const isVolunteer = (staffPositionId) => Boolean(positionOptions.find((option) => option.value === staffPositionId)?.volunteer);
  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const updatePosition = (key, changes) => setPositions((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)));

  const addPosition = () =>
    setPositions((current) => [...current, { key: `new-${Date.now()}-${current.length}`, id: null, staff_position_id: "", primary: current.length === 0, hourly_rate: "" }]);

  const removePosition = (row) => {
    if (row.id) setRemovedIds((current) => [...current, row.id]);
    setPositions((current) => {
      const remaining = current.filter((item) => item.key !== row.key);
      if (row.primary && remaining.length > 0) remaining[0] = { ...remaining[0], primary: true };
      return remaining;
    });
  };

  const makePrimary = (key) => setPositions((current) => current.map((row) => ({ ...row, primary: row.key === key })));

  const pinValid = form.pin === "" || PIN_PATTERN.test(form.pin);
  const canSubmit = form.first_name.trim().length > 0 && pinValid && positions.every((row) => row.staff_position_id);

  const payload = () => {
    const attributes = { ...form };
    if (!attributes.pin) delete attributes.pin;
    attributes.venue_ownership_ids = venueIds;
    attributes.staff_member_positions_attributes = [
      ...positions.map((row) => {
        const positionAttributes = { id: row.id, staff_position_id: row.staff_position_id, primary: row.primary };
        if (canSetRate(row.staff_position_id) && !isVolunteer(row.staff_position_id)) {
          positionAttributes.hourly_rate = row.hourly_rate === "" ? null : row.hourly_rate;
        }
        return positionAttributes;
      }),
      ...removedIds.map((id) => ({ id, _destroy: true })),
    ];
    return { staff_member: attributes };
  };

  const request = async (send, successMessage) => {
    setSaving(true);
    setErrors([]);
    try {
      await send();
      toastSuccess(successMessage);
      onSaved();
      onClose();
    } catch (error) {
      setErrors(errorMessages(error));
      toastError("Could not save this person.");
    } finally {
      setSaving(false);
    }
  };

  const updatePath = `${paths.create}/${person.id}`;
  const save = () => {
    if (!canSubmit) return;
    request(() => (isNew ? client.post(paths.create, payload()) : client.patch(updatePath, payload())), isNew ? "Person added." : "Changes saved.");
  };
  const setStatus = (status) =>
    request(() => client.patch(updatePath, { staff_member: { status } }), status === "archived" ? "Person archived." : "Person restored.");

  return (
    <Drawer
      open
      backdrop
      width={600}
      title={isNew ? "Add a person" : person.full_name}
      subtitle={isNew ? "Paid staff or volunteer." : undefined}
      onClose={onClose}
      footer={
        <div className="d-flex w-100" style={{ gap: 8 }}>
          {!isNew && !detailsLocked && person.status === "active" && (
            <Button variant="secondary" disabled={saving} onClick={() => setStatus("archived")}>
              Archive
            </Button>
          )}
          {!isNew && !detailsLocked && person.status === "archived" && (
            <Button variant="secondary" disabled={saving} onClick={() => setStatus("active")}>
              Restore
            </Button>
          )}
          <div className="ml-auto d-flex" style={{ gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!canSubmit || saving} onClick={save}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      }
    >
      {errors.length > 0 && (
        <div className="mb-3">
          <Alert color="error" title="Could not save this person" description={errors.join(" ")} />
        </div>
      )}

      {detailsLocked && (
        <div className="mb-3">
          <Alert
            color="gray"
            title="You can only change this person's positions you manage"
            description="They also work positions you don't manage, so their name, contact details, venues, PIN and status are locked."
          />
        </div>
      )}

      <div className="d-flex mb-3" style={{ gap: 12 }}>
        <div className="flex-fill">
          <TextInput
            id="staff-member-first-name"
            label="First name"
            required
            disabled={detailsLocked}
            value={form.first_name}
            onChange={(event) => setField("first_name", event.target.value)}
          />
        </div>
        <div className="flex-fill">
          <TextInput id="staff-member-last-name" label="Last name" disabled={detailsLocked} value={form.last_name} onChange={(event) => setField("last_name", event.target.value)} />
        </div>
      </div>
      <div className="d-flex mb-3" style={{ gap: 12 }}>
        <div className="flex-fill">
          <TextInput id="staff-member-email" label="Email" type="email" disabled={detailsLocked} value={form.email} onChange={(event) => setField("email", event.target.value)} />
        </div>
        <div className="flex-fill">
          <TextInput id="staff-member-phone" label="Mobile phone" type="tel" disabled={detailsLocked} value={form.phone} onChange={(event) => setField("phone", event.target.value)} />
        </div>
      </div>

      <div className="mb-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <strong>Positions</strong>
          <Button variant="link-color" size="sm" icon={Plus} onClick={addPosition}>
            Add position
          </Button>
        </div>
        {positions.length === 0 && <p className="text-muted mb-0">No positions yet. Add the jobs this person works.</p>}
        {positions.map((row, index) => (
          <div key={row.key} className="d-flex align-items-end mb-2 staff-member-position-row" style={{ gap: 8 }}>
            <div className="flex-fill">
              <Select
                id={`staff-member-position-${index}`}
                label={index === 0 ? "Position" : undefined}
                searchable
                disabled={lockedRow(row)}
                options={positionOptions}
                value={row.staff_position_id}
                onChange={(value) => updatePosition(row.key, { staff_position_id: value, hourly_rate: "" })}
              />
            </div>
            {canSetRate(row.staff_position_id) && (
              <div style={{ width: 150 }}>
                {row.staff_position_id && isVolunteer(row.staff_position_id) ? (
                  <Badge color="purple">Volunteer</Badge>
                ) : (
                  <TextInput
                    id={`staff-member-rate-${index}`}
                    label={index === 0 ? "Own rate" : undefined}
                    type="number"
                    min={0}
                    step="0.01"
                    leadingText="$"
                    placeholder="Default"
                    value={row.hourly_rate}
                    onChange={(event) => updatePosition(row.key, { hourly_rate: event.target.value })}
                  />
                )}
              </div>
            )}
            {!canSetRate(row.staff_position_id) && row.staff_position_id && isVolunteer(row.staff_position_id) && <Badge color="purple">Volunteer</Badge>}
            <Radio id={`staff-member-primary-${index}`} name="staff-member-primary" label="Primary" checked={row.primary} onChange={() => makePrimary(row.key)} />
            {!lockedRow(row) && <Button variant="tertiary" size="sm" icon={Trash01} aria-label="Remove position" onClick={() => removePosition(row)} />}
          </div>
        ))}
      </div>

      <div className="mb-3">
        <Select
          id="staff-member-venues"
          label="Venues"
          hint="The venues this person works at."
          multiple
          clearable
          disabled={detailsLocked}
          options={filterOptions.venues}
          value={venueIds.filter((id) => filterOptions.venues.some((option) => option.value === id))}
          onChange={(value) => setVenueIds([...venueIds.filter((id) => !filterOptions.venues.some((option) => option.value === id)), ...(value || [])])}
        />
      </div>

      <div className="mb-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <DatePicker
            id="staff-member-hired-on"
            label="Hire date"
            placeholder="Select a date"
            disabled={detailsLocked}
            parseDate={parseDateInput}
            value={toDate(form.hired_on)}
            onChange={(date) => setField("hired_on", toValue(date))}
          />
        </div>
        <div style={{ minWidth: 0 }}>
          <TextInput
            id="staff-member-payroll-id"
            label="Payroll ID"
            disabled={detailsLocked}
            value={form.payroll_external_id}
            onChange={(event) => setField("payroll_external_id", event.target.value)}
          />
        </div>
      </div>

      <TextInput
        id="staff-member-pin"
        label={person.pin_set ? "Reset time clock PIN" : "Time clock PIN"}
        hint={pinHint(person)}
        error={pinValid ? undefined : "Use 4 to 6 digits."}
        type="password"
        inputMode="numeric"
        autoComplete="new-password"
        disabled={detailsLocked}
        value={form.pin}
        onChange={(event) => setField("pin", event.target.value)}
      />
      {!isNew && paths.timeCards && (
        <div className="mt-4">
          <RecentTimeCards staffMemberId={person.id} paths={{ timeCards: paths.timeCards, timeCard: paths.timeCard }} />
        </div>
      )}
    </Drawer>
  );
};

PersonDrawer.propTypes = {
  person: PropTypes.shape({
    id: PropTypes.number,
    full_name: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    hired_on: PropTypes.string,
    payroll_external_id: PropTypes.string,
    pin_set: PropTypes.bool,
    pin_shared: PropTypes.bool,
    status: PropTypes.string,
    can_edit_details: PropTypes.bool,
    positions: PropTypes.arrayOf(PropTypes.object),
    venues: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default PersonDrawer;
